import { Toast, ToastInput, TOAST_QUEUE_LIMIT } from './toast.types';
import { toastDefaultDuration } from './toastVariant';

/**
 * The toast store: a plain module-level store read through `useSyncExternalStore`.
 *
 * Deliberately *not* a React context. Two reasons, in order of force:
 *
 * 1. `renderWithProviders` can't host a live region — `TablePill` already
 *    renders `role="status"`, so a global one would make `getByRole('status')`
 *    ambiguous in every test that renders a pill. With a module store, a test
 *    asserts `getToasts()` without rendering any toast UI at all.
 * 2. Plain modules (an axios interceptor, a print util) can raise a toast with
 *    no provider in scope.
 *
 * It is also *pure state*. Timers live in the card, because a store holding
 * `setTimeout` would leak a live timer per page test with nothing mounted to
 * consume it.
 */

const EMPTY: readonly Toast[] = Object.freeze([]);

let toasts: readonly Toast[] = EMPTY;
let nextId = 1;
const listeners = new Set<() => void>();

const emit = (next: readonly Toast[]): void => {
  toasts = next;
  listeners.forEach((listener) => listener());
};

export const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Must stay referentially stable between mutations. Returning a fresh array
 * here (a `.filter`, a spread) is the classic `useSyncExternalStore` infinite
 * render — hence the cached `toasts` above and the assertion in the tests.
 */
export const getSnapshot = (): readonly Toast[] => toasts;

/**
 * Two toasts collapse when they'd say the same thing. Without an explicit
 * `dedupeKey` the content itself is the key, which is what stops a
 * double-clicked save or a repeated barcode scan from stacking identical cards.
 */
const resolveDedupeKey = (input: ToastInput, id: number): string => {
  if (input.dedupeKey) return input.dedupeKey;
  if (input.allowDuplicate) return `unique:${id}`;
  return `${input.variant}|${input.title}|${input.description ?? ''}`;
};

export const showToast = (input: ToastInput): number => {
  const duration =
    input.duration === undefined
      ? toastDefaultDuration(input.variant, Boolean(input.action))
      : input.duration;

  // Errors are the ones people escalate, so they carry a copy button unless
  // the caller says otherwise.
  const copyable = input.copyable ?? input.variant === 'error';

  const dedupeKey = resolveDedupeKey(input, nextId);
  const existing = toasts.find(
    (toast) => toast.dedupeKey === dedupeKey && toast.status !== 'exiting'
  );

  // A match is updated in place and its timer restarted, so a progressing
  // operation ("Export in progress…" → "Export ready") reads as one card
  // changing rather than two cards disagreeing.
  if (existing) {
    emit(
      toasts.map((toast) =>
        toast.id === existing.id
          ? {
              ...toast,
              variant: input.variant,
              title: input.title,
              description: input.description,
              action: input.action,
              duration,
              copyable,
              sequence: toast.sequence + 1,
            }
          : toast
      )
    );
    return existing.id;
  }

  const toast: Toast = {
    id: nextId,
    variant: input.variant,
    title: input.title,
    description: input.description,
    action: input.action,
    duration,
    dedupeKey,
    copyable,
    status: 'visible',
    sequence: 0,
  };
  nextId += 1;

  // Backstop for contexts with no viewport mounted, where nothing ever
  // dismisses: drop the oldest rather than growing without bound.
  const next = [...toasts, toast];
  emit(next.length > TOAST_QUEUE_LIMIT ? next.slice(next.length - TOAST_QUEUE_LIMIT) : next);
  return toast.id;
};

/** Starts the exit animation. The card calls `removeToast` when it finishes. */
export const dismissToast = (id: number): void => {
  if (!toasts.some((toast) => toast.id === id && toast.status !== 'exiting')) return;
  emit(toasts.map((toast) => (toast.id === id ? { ...toast, status: 'exiting' } : toast)));
};

export const removeToast = (id: number): void => {
  if (!toasts.some((toast) => toast.id === id)) return;
  emit(toasts.filter((toast) => toast.id !== id));
};

/** Dismisses whichever toast currently holds this key, if any. */
export const dismissToastByKey = (key: string): void => {
  const match = toasts.find((toast) => toast.dedupeKey === key && toast.status !== 'exiting');
  if (match) dismissToast(match.id);
};

export const dismissAllToasts = (): void => {
  if (toasts.length === 0) return;
  emit(toasts.map((toast) => ({ ...toast, status: 'exiting' as const })));
};

/**
 * Test-only reset. Deliberately not wired into `setupTests.ts` — a global
 * `afterEach` reaching into this module would throw for any test that partially
 * mocks it. Call it from `beforeEach`, or use `resetToasts` in
 * `src/test-utils/toast.ts`.
 */
export const resetToastStoreForTests = (): void => {
  toasts = EMPTY;
  nextId = 1;
  listeners.clear();
};
